import React, { useState, useEffect, useRef, useMemo, useCallback } from "react"
import { Table, Dropdown } from "antd"
import PropTypes from "prop-types"
import { Icon } from "ui/Icon"
import { columns } from "pagesV2/pro/Experiences/ListingScheduling/LengthAndRates/PricingTable/Columns"
import { EditPricesForm } from "../EditPricesForm/EditPricesForm"
import { PRICING_TYPES, TRIP_PRICE } from "pagesV2/pro/Experiences/ListingScheduling/utils/constants"
import { durationFormatter } from "utils/durationFormatter"
import { useTranslation } from "react-i18next"
import { DeleteConfirmation } from "pagesV2/pro/DigitalWaiverAndContracts/DocumentsAdded/Deleteconfirmation"
import styles from "./LocalPricingTable.module.css"
import legacyStyles from "pagesV2/pro/Experiences/ListingScheduling/LengthAndRates/PricingTable/PricingTable.module.css"

function formatPrice(val) {
  if (val == null) return null
  return `$${val}`
}

function buildPerPersonSummary(record) {
  const parts = []
  if (record[TRIP_PRICE.PRICE_ADULT] != null) parts.push(`Adults ${formatPrice(record[TRIP_PRICE.PRICE_ADULT])}`)
  if (record[TRIP_PRICE.CHILDREN_ALLOWED] && record[TRIP_PRICE.PRICE_CHILD] != null) parts.push(`Children ${formatPrice(record[TRIP_PRICE.PRICE_CHILD])}`)
  if (record[TRIP_PRICE.INFANTS_ALLOWED] && record[TRIP_PRICE.PRICE_INFANT] != null) parts.push(`Infants ${formatPrice(record[TRIP_PRICE.PRICE_INFANT])}`)
  if (record[TRIP_PRICE.PASSENGERS_ALLOWED] && record[TRIP_PRICE.PRICE_PASSENGER] != null) parts.push(`Passengers ${formatPrice(record[TRIP_PRICE.PRICE_PASSENGER])}`)
  const pricePart = parts.join(" / ")
  const bufferPart = record[TRIP_PRICE.BUFFER_TIME] ? `Buffer ${durationFormatter(record[TRIP_PRICE.BUFFER_TIME])}` : ""
  return [pricePart, bufferPart].filter(Boolean).join(" · ")
}

function buildFlatRateSummary(record) {
  const parts = []
  if (record[TRIP_PRICE.FLAT_RATE] != null) parts.push(`Base rate ${formatPrice(record[TRIP_PRICE.FLAT_RATE])}`)
  if (record[TRIP_PRICE.GUESTS_INCLUDED] != null) parts.push(`Guests included: ${record[TRIP_PRICE.GUESTS_INCLUDED]}`)
  const pricePart = parts.join(" / ")
  const bufferPart = record[TRIP_PRICE.BUFFER_TIME] ? `Buffer ${durationFormatter(record[TRIP_PRICE.BUFFER_TIME])}` : ""
  return [pricePart, bufferPart].filter(Boolean).join(" · ")
}

function buildRowTitle(record, pricingType) {
  const recordPricingType = record.pricing_type || pricingType
  const isPerPerson = recordPricingType === PRICING_TYPES.PER_PERSON
  const typeLabel = isPerPerson ? "Per person" : "Flat rate"
  if (!record[TRIP_PRICE.DURATION]) return typeLabel
  return `${durationFormatter(record[TRIP_PRICE.DURATION])} · ${typeLabel}`
}

function LegacyExpandIcon({ expanded, onExpand, record }) {
  return (
    <Icon
      type="feather"
      name={expanded ? "ChevronUp" : "ChevronDown"}
      color="currentColor"
      width={20}
      height={20}
      onClick={(e) => onExpand(record, e)}
    />
  )
}

LegacyExpandIcon.propTypes = {
  expanded: PropTypes.bool,
  onExpand: PropTypes.func.isRequired,
  record: PropTypes.object.isRequired,
}

function WizardPriceRow({ record, pricingType, onEditPrice, onPriceDelete, deleteLabel }) {
  const { t } = useTranslation()
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const recordPricingType = record.pricing_type || pricingType
  const isPerPerson = recordPricingType === PRICING_TYPES.PER_PERSON
  const title = buildRowTitle(record, pricingType)
  const subtitle = isPerPerson ? buildPerPersonSummary(record) : buildFlatRateSummary(record)

  const confirmDelete = () => {
    const isServerPrice = typeof record.id === "number"
    if (isServerPrice) {
      onPriceDelete({ id: record.id })
    } else {
      onPriceDelete({ tempId: record.tempId })
    }
    setShowDeleteConfirm(false)
  }

  const dropdownItems = [{ key: "delete", label: deleteLabel, danger: true, onClick: () => setShowDeleteConfirm(true) }]

  return (
    <div className={styles.row}>
      <div className={styles.rowHeader}>
        <div className={styles.rowContent}>
          <div className={styles.rowTitle}>{title}</div>
          {subtitle && <div className={styles.rowSubtitle}>{subtitle}</div>}
        </div>
        <div className={styles.rowActions}>
          <Icon
            type="feather"
            name="Edit2"
            color="currentColor"
            width={20}
            height={20}
            className={styles.actionIcon}
            onClick={() => onEditPrice(record)}
          />
          <Dropdown menu={{ items: dropdownItems }} trigger={["click"]} placement="bottomRight">
            <span>
              <Icon
                type="feather"
                name="MoreHorizontal"
                color="currentColor"
                width={20}
                height={20}
                className={styles.actionIcon}
              />
            </span>
          </Dropdown>
        </div>
      </div>
      <DeleteConfirmation
        visible={showDeleteConfirm}
        title={t("experiences.pricing.private.delete_booking_option_title")}
        content={t("experiences.pricing.private.delete_booking_option_content")}
        confirmText={t("experiences.pricing.private.delete_booking_option_confirm")}
        cancelText={t("experiences.pricing.adjustment_modal.cancel")}
        onCancel={() => setShowDeleteConfirm(false)}
        onConfirm={confirmDelete}
      />
    </div>
  )
}

WizardPriceRow.propTypes = {
  record: PropTypes.object.isRequired,
  pricingType: PropTypes.string,
  onEditPrice: PropTypes.func.isRequired,
  onPriceDelete: PropTypes.func.isRequired,
  deleteLabel: PropTypes.string.isRequired,
}

/**
 * Wizard card-list view (isWizardLayout=true): each saved price renders as a summary row
 * with pencil (edit) and "..." (delete) actions. Draft rows open directly in edit form.
 *
 * Legacy table view (isWizardLayout=false): original Ant Design expandable table, used
 * by the old ListingScheduling flow.
 */
export const LocalPricingTable = ({
  prices,
  draftPrices,
  setDraftPrices,
  addNewPrice,
  setPricingType,
  isFlatRate,
  pricingType,
  expandedRowData,
  setExpandedRowData,
  form,
  onPriceSave,
  onPriceDelete,
  isWizardLayout,
  onEditPrice,
}) => {
  const { t } = useTranslation()
  const [expandedRowKeys, setExpandedRowKeys] = useState([])
  const lastExpandedId = useRef(null)

  const combinedData = [...prices, ...draftPrices]
  const filteredData = pricingType
    ? combinedData.filter((item) => item.pricing_type === pricingType || !item.pricing_type)
    : combinedData

  // Must be before any conditional returns — rules of hooks
  const filteredColumns = useMemo(() => {
    if (pricingType === "charge_per_person") {
      return columns.filter((col) => col.key !== "flat_rate")
    }
    return columns
  }, [pricingType])

  const closeEditForm = useCallback(() => {
    setExpandedRowKeys([])
    setExpandedRowData({})
  }, [setExpandedRowData])

  const legacyExpandedRowRender = useCallback(
    (record) => (
      <EditPricesForm
        record={record}
        key={record.id ?? record.tempId}
        expandedRowData={expandedRowData}
        isFlatRate={isFlatRate}
        closeEditForm={closeEditForm}
        setDraftPrices={setDraftPrices}
        addNewPrice={addNewPrice}
        form={form}
        onPriceSave={onPriceSave}
        onPriceDelete={onPriceDelete}
      />
    ),
    [expandedRowData, isFlatRate, closeEditForm, setDraftPrices, addNewPrice, form, onPriceSave, onPriceDelete]
  )

  // Auto-expand the newest draft row (legacy table only)
  useEffect(() => {
    if (isWizardLayout || draftPrices.length === 0) return
    const newestRow = draftPrices[draftPrices.length - 1]
    if (newestRow.id !== lastExpandedId.current) {
      setExpandedRowKeys([newestRow.id])
      setExpandedRowData(newestRow)
      if (newestRow.pricing_type) setPricingType(newestRow.pricing_type)
      lastExpandedId.current = newestRow.id
    }
  }, [draftPrices, prices, setPricingType, isWizardLayout, setExpandedRowData])

  const rowKey = (record) => record.id ?? record.tempId

  // ─── Wizard card-list view ───────────────────────────────────────────────
  if (isWizardLayout) {
    return (
      <div className={styles.list}>
        {filteredData.map((record) => (
          <WizardPriceRow
            key={rowKey(record)}
            record={record}
            pricingType={pricingType}
            onEditPrice={onEditPrice}
            onPriceDelete={onPriceDelete}
            deleteLabel={t("global.delete")}
          />
        ))}
      </div>
    )
  }

  // ─── Legacy expandable table view ────────────────────────────────────────
  return (
    <div className={legacyStyles.responsiveTableContainer}>
      <Table
        dataSource={filteredData}
        columns={filteredColumns}
        expandable={{
          expandedRowKeys,
          expandedRowRender: legacyExpandedRowRender,
          rowExpandable: () => true,
          expandedRowClassName: () => legacyStyles.expandedRow,
          expandIcon: LegacyExpandIcon,
          onExpand: (expanded, record) => {
            setExpandedRowKeys(expanded ? [rowKey(record)] : [])
            setExpandedRowData(expanded ? record : null)
            if (record.pricing_type) setPricingType(record.pricing_type)
          }
        }}
        pagination={false}
        rowHoverable={false}
        size="middle"
        className={legacyStyles.customTable}
        rowClassName={legacyStyles.customTableRow}
        rowKey={rowKey}
      />
    </div>
  )
}

LocalPricingTable.propTypes = {
  prices: PropTypes.arrayOf(PropTypes.object).isRequired,
  draftPrices: PropTypes.arrayOf(PropTypes.object).isRequired,
  setDraftPrices: PropTypes.func.isRequired,
  addNewPrice: PropTypes.func.isRequired,
  setPricingType: PropTypes.func.isRequired,
  isFlatRate: PropTypes.bool,
  pricingType: PropTypes.string,
  form: PropTypes.object.isRequired,
  expandedRowData: PropTypes.object,
  setExpandedRowData: PropTypes.func,
  onPriceSave: PropTypes.func.isRequired,
  onPriceDelete: PropTypes.func.isRequired,
  isWizardLayout: PropTypes.bool,
  onEditPrice: PropTypes.func,
}
